import { useState, useRef, useEffect } from 'react';
import { Pencil, Users, Shield, Trash2, Palette, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const hubColors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899'];

// Edit Hub Modal
export const EditHubModal = ({ 
  isOpen, 
  onClose, 
  hub,
  token,
  onUpdate 
}) => {
  const [hubName, setHubName] = useState(hub?.name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(hub?.logo || null);
  const fileInputRef = useRef(null);

  const handleUpdate = async () => {
    if (!hubName.trim() || !hub) return;
    setIsLoading(true);
    try {
      await onUpdate?.(hub.id, hubName);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/hubs/${hub.id}/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setLogoPreview(data.logo);
        toast.success('Logo uploaded successfully');
      } else {
        toast.error(data.detail || 'Failed to upload logo');
      }
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoPreview) return;
    
    setIsUploadingLogo(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/hubs/${hub.id}/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setLogoPreview(null);
        toast.success('Logo removed');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove logo');
      }
    } catch (err) {
      toast.error('Failed to remove logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Reset state when modal opens with new hub
  useState(() => {
    if (hub) {
      setHubName(hub.name || '');
      setLogoPreview(hub.logo || null);
    }
  }, [hub]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Hub</DialogTitle>
          <DialogDescription className="text-white/60">
            Customize your hub&apos;s appearance and settings.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="flex flex-col items-center gap-3">
            {/* Logo/Avatar Preview */}
            <div className="relative group">
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt={hub?.name}
                  className="w-20 h-20 rounded-xl object-cover cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleLogoClick}
                />
              ) : (
                <div 
                  className="w-20 h-20 rounded-xl flex items-center justify-center text-white text-2xl font-bold cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: hub?.color || '#F97316' }}
                  onClick={handleLogoClick}
                >
                  {hubName?.charAt(0)?.toUpperCase() || 'H'}
                </div>
              )}
              
              {/* Upload overlay */}
              {isUploadingLogo && (
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
              
              {/* Remove button */}
              {logoPreview && !isUploadingLogo && (
                <button
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove logo"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
              data-testid="hub-logo-input"
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              className="text-white/70 border-white/20 hover:bg-white/10"
              onClick={handleLogoClick}
              disabled={isUploadingLogo}
              data-testid="upload-hub-logo-btn"
            >
              <Upload size={14} className="mr-2" />
              {isUploadingLogo ? 'Uploading...' : (logoPreview ? 'Change Logo' : 'Upload Logo')}
            </Button>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hub-name" className="text-white/80">Hub Name</Label>
            <Input
              id="hub-name"
              value={hubName}
              onChange={(e) => setHubName(e.target.value)}
              placeholder="Enter hub name"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="edit-hub-name-input"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!hubName.trim() || isLoading}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="save-hub-changes-btn"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Manage Members Modal
export const ManageMembersModal = ({ 
  isOpen, 
  onClose, 
  hub,
  members,
  currentUser,
  token,
  onInvite,
  onRemoveMember 
}) => {
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);

  // Fetch pending invitations when modal opens
  const fetchPendingInvitations = async () => {
    if (!hub?.id || !token) return;
    setIsLoadingInvites(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invitations/sent?target_type=hub&target_id=${hub.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        // Filter for pending invitations only
        setPendingInvitations(data.filter(inv => inv.status === 'pending'));
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    } finally {
      setIsLoadingInvites(false);
    }
  };

  // Fetch invitations when modal opens
  useEffect(() => {
    if (isOpen && hub?.id) {
      fetchPendingInvitations();
    }
  }, [isOpen, hub?.id]);

  const handleInvite = async () => {
    if (!inviteEmails.trim()) return;
    await onInvite?.(inviteEmails, inviteRole);
    setInviteEmails('');
    // Refresh invitations list
    fetchPendingInvitations();
  };

  const handleResendInvite = async (invitationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/resend/${invitationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Invitation resent');
        fetchPendingInvitations();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to resend invitation');
      }
    } catch (err) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Invitation revoked');
        fetchPendingInvitations();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to revoke invitation');
      }
    } catch (err) {
      toast.error('Failed to revoke invitation');
    }
  };

  const copyInviteLink = async (inviteLink) => {
    const fullLink = `${window.location.origin}${inviteLink}`;
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success('Invite link copied!');
    } catch (err) {
      toast.info('Press Ctrl+C to copy');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div 
              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: hub?.color || '#F97316' }}
            >
              {hub?.name?.charAt(0)?.toUpperCase()}
            </div>
            Manage Members
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Invite new members and manage existing ones.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Invite Section */}
          <div className="space-y-3">
            <Label className="text-white/80 text-sm font-medium">Invite New Members</Label>
            <div className="flex gap-2">
              <Input
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                placeholder="Enter email addresses (comma separated)"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 flex-1"
                data-testid="invite-email-input"
              />
              <select 
                value={inviteRole} 
                onChange={(e) => setInviteRole(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-md px-3 text-white text-sm"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <Button 
              onClick={handleInvite}
              disabled={!inviteEmails.trim()}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="send-invite-btn"
            >
              Send Invite
            </Button>
          </div>
          
          {/* Pending Invitations - From API */}
          {pendingInvitations.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white/80 text-sm font-medium">
                Pending Invitations ({pendingInvitations.length})
              </Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {pendingInvitations.map(invite => (
                  <div key={invite.id} className="flex items-center justify-between p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 text-sm font-medium">
                        {invite.email?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white text-sm">{invite.email}</p>
                        <p className="text-yellow-400 text-xs capitalize">{invite.role} • Pending</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-blue-400 hover:text-blue-300 h-7 px-2"
                        onClick={() => copyInviteLink(invite.invite_link)}
                        title="Copy invite link"
                      >
                        Copy
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-white/50 hover:text-white h-7 px-2"
                        onClick={() => handleResendInvite(invite.id)}
                        title="Resend invitation"
                      >
                        Resend
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300 h-7 px-2"
                        onClick={() => handleRevokeInvite(invite.id)}
                        title="Revoke invitation"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLoadingInvites && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="w-5 h-5 animate-spin text-white/50" />
              <span className="ml-2 text-white/50 text-sm">Loading invitations...</span>
            </div>
          )}
          
          {/* Current Members */}
          <div className="space-y-2">
            <Label className="text-white/80 text-sm font-medium">
              Current Members ({members?.length || 0})
            </Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {members?.map(member => (
                <div key={member.user_id || member.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: member.avatar_color || '#6366F1' }}
                    >
                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-white text-sm">{member.name || 'Unknown'}</p>
                      <p className="text-white/50 text-xs">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "px-2 py-0.5 rounded text-xs",
                      member.role === 'admin' ? 'bg-purple-500/20 text-purple-300' : 'bg-white/10 text-white/60'
                    )}>
                      {member.role}
                    </span>
                    {(member.user_id || member.id) !== currentUser?.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300 h-7 px-2"
                        onClick={() => onRemoveMember?.(member.user_id || member.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {(!members || members.length === 0) && (
                <p className="text-white/50 text-sm text-center py-4">No members yet</p>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Board Permissions Modal
export const BoardPermissionsModal = ({ 
  isOpen, 
  onClose, 
  hub,
  boards 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} className="text-blue-400" />
            Board Permissions
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Control which members can view and edit boards in this hub.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {boards?.length > 0 ? (
            <div className="space-y-3">
              {boards.map(board => (
                <div key={board.id} className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Palette size={16} className="text-blue-400" />
                      <span className="text-white font-medium">{board.name || 'Untitled Canvas'}</span>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-white/70">
                      <input type="checkbox" defaultChecked className="rounded bg-white/10 border-white/20" />
                      All members can view
                    </label>
                    <label className="flex items-center gap-2 text-sm text-white/70">
                      <input type="checkbox" defaultChecked className="rounded bg-white/10 border-white/20" />
                      All members can edit
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-white/50">
              <Palette size={32} className="mx-auto mb-2 opacity-50" />
              <p>No boards in this hub yet</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={onClose}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Save Permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete Hub Modal
export const DeleteHubModal = ({ 
  isOpen, 
  onClose, 
  hub,
  onDelete 
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== hub?.name) return;
    setIsLoading(true);
    try {
      await onDelete?.(hub.id);
      onClose();
    } finally {
      setIsLoading(false);
      setConfirmText('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400 flex items-center gap-2">
            <Trash2 size={18} />
            Delete Hub
          </DialogTitle>
          <DialogDescription className="text-white/60">
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold"
              style={{ backgroundColor: hub?.color || '#F97316' }}
            >
              {hub?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{hub?.name}</p>
              <p className="text-white/50 text-sm">All channels, boards, and data will be permanently deleted.</p>
            </div>
          </div>
          
          <p className="text-white/70 text-sm">
            Type <span className="font-mono text-red-400">&quot;{hub?.name}&quot;</span> to confirm deletion:
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type hub name to confirm"
            className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-white/40"
          />
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={confirmText !== hub?.name || isLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isLoading ? 'Deleting...' : 'Delete Hub'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
